import React, { useState, useEffect } from 'react';
import { Search, Download, FileText, Sparkles, Filter } from 'lucide-react';
import HeaderRouter from '../components/HeaderRouter';
import Footer from '../components/Footer';
import { 
  getPublicPrintables, 
  getPrintablesMetaOptions, 
  trackPrintableDownload 
} from '../api/studentApi';
import './PrintablesPage.css';

const PrintablesPage = () => {
  const [printables, setPrintables] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGradeId, setSelectedGradeId] = useState('ALL');
  const [selectedSubjectId, setSelectedSubjectId] = useState('ALL');

  useEffect(() => {
    fetchPublicData();
  }, []);

  const fetchPublicData = async () => {
    setLoading(true);
    try {
      const [printablesRes, metaOptionsRes] = await Promise.all([
        getPublicPrintables(),
        getPrintablesMetaOptions(),
      ]);

      setPrintables(printablesRes.data || []);
      
      const curriculum = metaOptionsRes.data || {};
      setGrades(curriculum.grades || []);
      setSubjects(curriculum.subjects || []);
    } catch (err) {
      console.error('Failed to load printable resources:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle Download / Print Action
  const handleAction = async (item) => {
    // 1. Increment download count silently via API helper
    try {
      trackPrintableDownload(item.id).catch(() => {});
    } catch (e) {
      /* silent catch */
    }

    // 2. Direct Static PDF File Link Download
    if (item.pdfUrl) {
      const link = document.createElement('a');
      link.href = item.pdfUrl;
      link.target = '_blank';
      link.download = `${item.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // // 3. Dynamic Template Printing
    // let htmlContent = '';
    // if (item.templateKey === 'MULTIPLICATION_TABLES') {
    //   htmlContent = generateMultiplicationHtml(item.metadata);
    // }

    // if (htmlContent) {
    //   const printFrame = document.createElement('iframe');
    //   printFrame.style.position = 'fixed';
    //   printFrame.style.right = '0';
    //   printFrame.style.bottom = '0';
    //   printFrame.style.width = '0px';
    //   printFrame.style.height = '0px';
    //   printFrame.style.border = '0';

    //   document.body.appendChild(printFrame);

    //   const frameDoc = printFrame.contentWindow.document;
    //   frameDoc.open();
    //   frameDoc.write(htmlContent);
    //   frameDoc.close();

    //   printFrame.contentWindow.focus();
    //   setTimeout(() => {
    //     printFrame.contentWindow.print();
    //     document.body.removeChild(printFrame);
    //   }, 400);
    // }
  };

  // Dynamic Subject filtering based on selected Grade
  const filteredSubjects = selectedGradeId === 'ALL'
    ? subjects
    : subjects.filter((s) => String(s.gradeId) === String(selectedGradeId));

  // Filter Printables Grid
  const filteredPrintables = printables.filter((item) => {
    const matchesSearch =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesGrade = selectedGradeId === 'ALL' || item.gradeId === Number(selectedGradeId);
    const matchesSubject = selectedSubjectId === 'ALL' || item.subjectId === Number(selectedSubjectId);

    return matchesSearch && matchesGrade && matchesSubject;
  });

  return (
    <div className="landing-wrapper">
      <HeaderRouter/ >

      <main className="tos-container">
        {/* Hero Section */}
        <div className="printables-hero">
          <div className="hero-badge">
            <Sparkles size={16} /> Free Learning Resources
          </div>
          <h1>Printable Worksheets & Study Guides</h1>
          <p>
            High-quality, ready-to-print reference sheets, multiplication charts, and practice guides designed for students.
          </p>

          <div className="hero-search-wrapper">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by topic, keyword, or title (e.g. Multiplication, Clocks)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Navigation Bar */}
        <div className="printables-filter-bar">
          <div className="filter-label">
            <Filter size={16} /> Filter By:
          </div>

          <div className="filter-selects">
            <select
              value={selectedGradeId}
              onChange={(e) => {
                setSelectedGradeId(e.target.value);
                setSelectedSubjectId('ALL');
              }}
            >
              <option value="ALL">All Grades</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            <select
              value={selectedSubjectId}
              onChange={(e) => setSelectedSubjectId(e.target.value)}
            >
              <option value="ALL">All Subjects</option>
              {filteredSubjects.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grid Display */}
        {loading ? (
          <div className="printables-loading">
            <div className="spinner"></div>
            <p>Loading printable resources...</p>
          </div>
        ) : (
          <div className="printables-user-grid">
            {filteredPrintables.map((item) => (
              <div key={item.id} className="user-printable-card">
                {item.thumbnailUrl ? (
                  <div className="card-thumb-preview">
                    <img src={item.thumbnailUrl} alt={item.title} />
                  </div>
                ) : (
                  <div className="card-thumb-placeholder">
                    <FileText size={42} className="placeholder-icon" />
                  </div>
                )}

                <div className="card-body">
                  <div className="card-tags">
                    {item.grade?.name && <span className="tag tag-grade">{item.grade.name}</span>}
                    {item.subject?.name && <span className="tag tag-subject">{item.subject.name}</span>}
                  </div>

                  <h3>{item.title}</h3>
                  <p className="card-desc">
                    {item.description || 'Printable reference sheet for classroom practice and home revision.'}
                  </p>

                  <button
                    className="action-download-btn"
                    onClick={() => handleAction(item)}
                  >
                    <Download size={18} />
                    Download
                  </button>
                </div>
              </div>
            ))}

            {!loading && filteredPrintables.length === 0 && (
              <div className="empty-grid-state">
                <FileText size={48} />
                <h3>No printables found</h3>
                <p>Try selecting a different grade or subject filter above.</p>
              </div>
            )}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PrintablesPage;